import React, { memo, useContext } from "react"
import { useTranslation } from "react-i18next"
import { MessageGroup } from "@/reducers/messagesSlice"

import { Message, MessageImageGroup } from "@/components/ui/message/index"
import { useMessageData } from "@/components/chat/useMessageData"
import { useGroupedFiles } from "@/components/chat/useGroupedFiles"
import { ChatHistoryContext } from "@/contexts/ChatHistoryContext"
import { CornerUpRight } from "lucide-react"
import useGroupedMessageActions from "@/components/chat/useGroupedMessageActions"
import { useForwardedMessageData } from "@/components/chat/useForwardedMessageData"
import { useForwardedRoomLink } from "@/components/chat/useForwardedRoomLink"

type GroupedForwardedImagesMessageProps = {
  messagesGroup: MessageGroup
  interactive?: boolean
  actionButtonsVisible?: boolean
  hideActionButtons?: () => void
}

const GroupedForwardedImagesMessage: React.FC<
  GroupedForwardedImagesMessageProps
> = ({
  messagesGroup,
  interactive = true,
  actionButtonsVisible,
  hideActionButtons,
}) => {
  const { t } = useTranslation()
  const messageForRowAction = messagesGroup.messages[0].forwarded
  const messageData = useMessageData(messagesGroup.messages[0])

  const actions = useGroupedMessageActions(messagesGroup)

  const { isForwardingMode } = useContext(ChatHistoryContext)

  const forwardedMessages = messagesGroup.messages.map((m) => m.forwarded)

  const { expandImageGroup } = useContext(ChatHistoryContext)

  const handleExpand = () => {
    expandImageGroup(messagesGroup)
  }

  const images = useGroupedFiles(forwardedMessages, messageData.authorName)
  if (!messageForRowAction) return null

  const forwardedMessageData = useForwardedMessageData(messageForRowAction)
  const { roomName, navigateToRoom } = useForwardedRoomLink(
    messageForRowAction.from,
  )
  const author = messagesGroup?.messages[0]?.from?.startsWith("cmr")
    ? ` ${forwardedMessageData.authorName}`
    : ` ${t("chat.forwarded.label")}`

  return (
    <Message
      message={messageForRowAction}
      variant={messageData.variant}
      showAvatar={!isForwardingMode}
      interactive={interactive}
      onForward={!isForwardingMode ? actions.handleForward : undefined}
      actionButtonsVisible={actionButtonsVisible}
      hideActionButtons={hideActionButtons}
    >
      <Message.Avatar
        src={messageData.avatarSrc}
        name={messageData.authorName}
        fallback={messageData.authorInitials}
      />
      <Message.Bubble withDriver={messageData.isFromDriver}>
        <Message.Header author={messageData.authorName} showTimestamp={false} />
        <div>
          <div
            role={roomName ? "button" : undefined}
            onClick={roomName ? navigateToRoom : undefined}
            className={`flex gap-1 ${roomName ? "cursor-pointer hover:underline" : ""}`}
          >
            <CornerUpRight className="h-4 w-4 self-center text-muted-foreground" />
            <Message.Header
              author={author}
              showTimestamp={false}
              className="text-xs font-normal text-muted-foreground"
            />
          </div>
          <MessageImageGroup
            images={images}
            maxImages={4}
            gap="md"
            aspectRatio="square"
            onExpand={handleExpand}
          />
        </div>
      </Message.Bubble>
      {interactive && !isForwardingMode && <Message.Actions />}
    </Message>
  )
}

// Custom comparison function for GroupedForwardedImagesMessage
const areEqual = (
  prevProps: GroupedForwardedImagesMessageProps,
  nextProps: GroupedForwardedImagesMessageProps,
) => {
  return (
    prevProps.messagesGroup.groupId === nextProps.messagesGroup.groupId &&
    prevProps.messagesGroup.messages.length ===
      nextProps.messagesGroup.messages.length &&
    prevProps.interactive === nextProps.interactive
  )
}

export default memo(GroupedForwardedImagesMessage, areEqual)
